import React, { useEffect, useMemo, useState } from "react";
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import Jimp from "jimp";
import { closestColor } from "../helpers/helpers";

const BitmapLayerDebugModal = ({ isOpen, onDismiss, file, colors, crop }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [layerPreviews, setLayerPreviews] = useState([]);
  const [selectedLayerIndex, setSelectedLayerIndex] = useState(0);
  const [viewMode, setViewMode] = useState("mask"); // 'mask' | 'color'
  const [zoom, setZoom] = useState(1);
  const [alphaThreshold, setAlphaThreshold] = useState(1);
  const [algorithm, setAlgorithm] = useState("closest");
  const [tolerance, setTolerance] = useState(10);

  const parsedColors = useMemo(() => {
    if (!colors || !colors.length) return [];
    return colors
      .map((c) => (c && c[0] ? c[0].replace("#", "") : ""))
      .filter((hex) => /^[0-9a-fA-F]{6}$/.test(hex))
      .map((hex) => [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ]);
  }, [colors]);

  const exactMatchWithTolerance = (intColor, COLORS, tol) => {
    const { r, g, b } = Jimp.intToRGBA(intColor);
    for (let i = 0; i < COLORS.length; i++) {
      if (
        Math.abs(r - COLORS[i][0]) <= tol &&
        Math.abs(g - COLORS[i][1]) <= tol &&
        Math.abs(b - COLORS[i][2]) <= tol
      ) {
        return { r: COLORS[i][0], g: COLORS[i][1], b: COLORS[i][2] };
      }
    }
    return false;
  };

  useEffect(() => {
    let cancelled = false;
    const generate = async () => {
      if (!isOpen || !file || !parsedColors.length) {
        setLayerPreviews([]);
        return;
      }
      setIsGenerating(true);
      try {
        const image = await Jimp.read(file);
        let processedImage = image;
        
        // Apply crop if provided
        if (crop && crop.width && crop.height && crop.x !== null && crop.y !== null) {
          processedImage = image.crop(crop.x, crop.y, crop.width, crop.height);
        }
        
        const width = processedImage.getWidth();
        const height = processedImage.getHeight();

        const previews = [];
        for (let layerIndex = 0; layerIndex < parsedColors.length; layerIndex++) {
          const maskImg = new Jimp(width, height, 0xffffffff);
          const colorImg = new Jimp(width, height, 0x00000000);
          let pixelCount = 0;

          for (let x = 0; x < width; x++) {
            for (let y = 0; y < height; y++) {
              const intCol = processedImage.getPixelColor(x, y);
              const rgba = Jimp.intToRGBA(intCol);
              if (rgba.a < alphaThreshold) continue;

              let pixelColorRGB;
              if (algorithm === "closest") {
                pixelColorRGB = closestColor(intCol, parsedColors, parsedColors.length);
              } else {
                pixelColorRGB = exactMatchWithTolerance(intCol, parsedColors, tolerance);
                if (pixelColorRGB === false) continue;
              }

              if (
                pixelColorRGB.r === parsedColors[layerIndex][0] &&
                pixelColorRGB.g === parsedColors[layerIndex][1] &&
                pixelColorRGB.b === parsedColors[layerIndex][2]
              ) {
                maskImg.setPixelColor(0x000000ff, x, y);
                const originalColor = Jimp.rgbaToInt(
                  parsedColors[layerIndex][0],
                  parsedColors[layerIndex][1],
                  parsedColors[layerIndex][2],
                  255
                );
                colorImg.setPixelColor(originalColor, x, y);
                pixelCount++;
              }
            }
          }

          const [maskSrc, colorSrc] = await Promise.all([
            new Promise((resolve) => maskImg.getBase64(Jimp.MIME_PNG, (_, src) => resolve(src))),
            new Promise((resolve) => colorImg.getBase64(Jimp.MIME_PNG, (_, src) => resolve(src))),
          ]);

          const hex = colors?.[layerIndex]?.[0]?.replace("#", "") || "";
          previews.push({ index: layerIndex, maskSrc, colorSrc, hex, pixelCount });
        }

        if (!cancelled) {
          setLayerPreviews(previews);
          if (previews.length > 0) setSelectedLayerIndex(0);
        }
      } catch (e) {
        console.error("BitmapLayerDebugModal: error generating previews", e);
      } finally {
        if (!cancelled) setIsGenerating(false);
      }
    };

    generate();
    return () => {
      cancelled = true;
    };
  }, [isOpen, file, parsedColors, colors, alphaThreshold, algorithm, tolerance, crop]);

  const selected = layerPreviews.find((l) => l.index === selectedLayerIndex);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss} backdropDismiss>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Bitmap Layers (debug)</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>Close</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <IonSelect interface="popover" value={algorithm} onIonChange={(e) => setAlgorithm(e.detail.value)}>
              <IonSelectOption value="closest">Closest</IonSelectOption>
              <IonSelectOption value="exact">Exact</IonSelectOption>
            </IonSelect>
            {algorithm === "exact" && (
              <>
                <span style={{ fontSize: "12px", color: "#666" }}>Tol</span>
                <input type="range" min="0" max="50" step="1" value={tolerance} onChange={(e) => setTolerance(parseInt(e.target.value))} />
                <span style={{ fontSize: "12px", color: "#666", width: 34, textAlign: "right" }}>{tolerance}</span>
              </>
            )}
            <span style={{ fontSize: "12px", color: "#666" }}>Alpha</span>
            <input type="range" min="0" max="50" step="1" value={alphaThreshold} onChange={(e) => setAlphaThreshold(parseInt(e.target.value))} />
            <span style={{ fontSize: "12px", color: "#666", width: 34, textAlign: "right" }}>{alphaThreshold}</span>
            <IonButton size="small" fill={viewMode === "mask" ? "solid" : "outline"} onClick={() => setViewMode("mask")}>Mask</IonButton>
            <IonButton size="small" fill={viewMode === "color" ? "solid" : "outline"} onClick={() => setViewMode("color")}>Color</IonButton>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto" }}>
              <span style={{ fontSize: "12px", color: "#666" }}>Zoom</span>
              <input type="range" min="0.25" max="4" step="0.05" value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} />
              <span style={{ fontSize: "12px", color: "#666", width: "40px", textAlign: "right" }}>{Math.round(zoom * 100)}%</span>
            </div>
          </div>

          <IonCard style={{ margin: 0 }}>
            <IonCardContent>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                <div style={{ width: "18px", height: "22px", backgroundColor: `#${selected?.hex || "000000"}`, border: "1px solid #979797", borderRadius: "2px" }} />
                <span style={{ fontSize: "14px", fontWeight: 600 }}>Layer {selected ? selected.index + 1 : "-"}</span>
                <span style={{ marginLeft: "auto", fontSize: "12px", color: selected?.pixelCount ? "#666" : "#d93025" }}>
                  {selected?.pixelCount ? `${selected.pixelCount.toLocaleString()} px` : "no pixels"}
                </span>
              </div>
              <div style={{ width: "100%", height: "70vh", display: "flex", alignItems: "center", justifyContent: "center", overflow: "auto", background: viewMode === "mask" ? "#fff" : "#f5f5f5", border: "1px solid #e5e5e5", borderRadius: "6px" }}>
                {isGenerating ? (
                  <div style={{ color: "#666", fontSize: "12px" }}>Generating bitmap layers...</div>
                ) : selected ? (
                  <img src={viewMode === "mask" ? selected.maskSrc : selected.colorSrc} alt="Selected layer large view" style={{ transform: `scale(${zoom})`, transformOrigin: "center center", objectFit: "contain", maxWidth: "100%", maxHeight: "100%" }} />
                ) : null}
              </div>
            </IonCardContent>
          </IonCard>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px" }}>
            {layerPreviews.map((l) => (
              <IonCard key={l.index} onClick={() => setSelectedLayerIndex(l.index)} style={{ margin: 0, border: selectedLayerIndex === l.index ? "2px solid #5282FF" : "1px solid #E2E2E2" }}>
                <IonCardContent>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                    <div style={{ width: "12px", height: "14px", backgroundColor: `#${l.hex}` , border: "1px solid #979797", borderRadius: "2px" }} />
                    <span style={{ fontSize: "12px" }}>Layer {l.index + 1}</span>
                    <span style={{ marginLeft: "auto", fontSize: "11px", color: l.pixelCount ? "#666" : "#d93025" }}>
                      {l.pixelCount ? `${l.pixelCount.toLocaleString()} px` : "no pixels"}
                    </span>
                  </div>
                  <div style={{ width: "100%", height: "110px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: viewMode === "mask" ? "#fff" : "#f5f5f5", border: "1px solid #e5e5e5", borderRadius: "4px" }}>
                    <img src={viewMode === "mask" ? l.maskSrc : l.colorSrc} alt={`Layer ${l.index + 1}`} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                  </div>
                </IonCardContent>
              </IonCard>
            ))}
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default BitmapLayerDebugModal; 